import { useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Stack } from '@mantine/core';
import { FieldConfig, SectionConfig } from '../types';
import SectionItem from './SectionItem';

type SectionListProps = {
  sections: SectionConfig[];
  onSectionsChange: (sections: SectionConfig[]) => void;
};

export default function SectionList({ sections, onSectionsChange }: SectionListProps) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = sections.findIndex(s => s.tableTag === active.id);
      const newIndex = sections.findIndex(s => s.tableTag === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      onSectionsChange(arrayMove(sections, oldIndex, newIndex));
    },
    [sections, onSectionsChange],
  );

  const handleToggleSectionVisible = useCallback(
    (index: number) => {
      const updated = sections.map((s, i) => (i === index ? { ...s, visible: !s.visible } : s));
      onSectionsChange(updated);
    },
    [sections, onSectionsChange],
  );

  const handleDisplayNameChange = useCallback(
    (sectionIndex: number, displayName: string | null) => {
      const updated = sections.map((s, i) => (i === sectionIndex ? { ...s, displayName } : s));
      onSectionsChange(updated);
    },
    [sections, onSectionsChange],
  );

  const handleFieldsChange = useCallback(
    (sectionIndex: number, fields: FieldConfig[]) => {
      const updated = sections.map((s, i) => (i === sectionIndex ? { ...s, fields } : s));
      onSectionsChange(updated);
    },
    [sections, onSectionsChange],
  );

  const sectionIds = sections.map(s => s.tableTag);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
        <Stack gap="xs">
          {sections.map((section, index) => (
            <SectionItem
              key={section.tableTag}
              section={section}
              onToggleVisible={() => handleToggleSectionVisible(index)}
              onDisplayNameChange={displayName => handleDisplayNameChange(index, displayName)}
              onFieldsChange={fields => handleFieldsChange(index, fields)}
            />
          ))}
        </Stack>
      </SortableContext>
    </DndContext>
  );
}
